<template>
    <div class="container">
        <div class="columns">
            <div class="column is-half">
                <div class="box">
                    <vue-web-cam
                        ref="webcam"
                        :device-id="deviceId"
                        :width="width"
                        :height="height"
                        @started="onStarted"
                        @stopped="onStopped"
                        @error="onError"
                        @cameras="onCameras"
                        @camera-change="onCameraChange"
                        @video-live="onVideoLive"
                    />
                </div>
                <div class="columns">
                  <div class="column is-narrow">
                    <p class="tight">{{pctFakeText}}</p>
                    <p class="tight">Fake</p>
                  </div>
                  <div class="column">
                    <div :class="gradientClass">
                      <div class="arrow-down" :style="markerStyle" />
                      <div class="inlinemarker" :style="markerStyle" />
                      <div class="arrow-up" :style="markerStyle" />
                      {{helperText}}
                    </div>
                  </div>
                  <div class="column is-narrow">
                    <p class="tight">{{pctRealText}}</p>
                    <p class="tight">Real</p>
                  </div>
                </div>
                <div>
                    <div>
                        <select v-model="deviceId">
                            <option disabled value="">-- Select Device --</option>
                            <option
                                v-for="d in devices"
                                :key="d.deviceId"
                                :value="d.deviceId"
                            >{{ d.label }}</option>
                        </select>
                    </div>
                    <div>
                        <button type="button" class="button is-danger" @click="onStop">Stop Camera</button>
                        <button type="button" class="button is-success" @click="onStart">Start Camera</button>
                    </div>
                </div>
            </div>
            <div class="column is-half">
                <h2>Captured Image</h2>
                <canvas id="imgcapture" />
            </div>
        </div>
    </div>
</template>

<script>
import { WebCam } from 'vue-web-cam';
import axios from 'axios';
import { mapState } from 'vuex';

let cv = null;

export default {
  name: 'App',
  components: {
    'vue-web-cam': WebCam
  },
  data() {
    return {
      deviceId: '',
      devices: [],
      videoCap: null,
      processTimer: null,
      width: 640,
      height: 480,
      helperText: 'loading...',
      lastUploadTime: 0,
      pctReal: 0,
      pctFake: 0,
      queryActive: false,
    };
  },
  computed: {
    ...mapState({ apiHost: 'apiHost', bearerToken: 'bearerToken' }),
    device() {
      return this.devices.find(n => n.deviceId === this.deviceId);
    },
    gradientClass() {
      return this.helperText ? 'gradientbarfaded' : 'gradientbar';
    },
    markerStyle() {
      return this.pctReal > 0 ? { left: `${Math.min(99, Math.round(this.pctReal * 100))}%` } : { display: 'none' };
    },
    pctRealText() {
      return this.pctReal > 0 ? `${(this.pctReal * 100).toFixed(1)}%` : '%';
    },
    pctFakeText() {
      return this.pctFake > 0 ? `${(this.pctFake * 100).toFixed(1)}%` : '%';
    }
  },
  methods: {
    onVideoLive() {
      if (window.Module) {
        cv = window.Module;
        this.startVideoProcessing();
      } else {
        this.loadOpenCv();
      }
    },
    loadOpenCv() {
      if (!window.WebAssembly) {
        console.warn("Your web browser doesn't support WebAssembly.");
        return;
      }
      console.log('loading OpenCv.js');
      window.Module = {
        wasmBinaryFile: `${process.env.BASE_URL}libs/opencv_js.wasm`, // for wasm mode
        _main: () => {
          console.log('OpenCV.js is ready.');
          cv = window.cv;
          this.startVideoProcessing();
        }
      };
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = 'async';
      script.src = `${process.env.BASE_URL}libs/opencv.js`;
      document.body.appendChild(script);
      script.onload = () => {
        console.log('OpenCV.js is loaded.');
      };
    },
    startVideoProcessing() {
      console.log('startVideoProcessing.');
      // if (!this.stream) {
      //   console.warn('Please startup your webcam.');
      //   return;
      // }
      this.srcMat = new cv.Mat(this.height, this.width, cv.CV_8UC4);
      this.dstC1Mat = new cv.Mat(this.height, this.width, cv.CV_8UC1);
      // this.dstC3Mat = new cv.Mat(this.height, this.width, cv.CV_8UC3);
      // this.dstC4Mat = new cv.Mat(this.height, this.width, cv.CV_8UC4);
      this.videoCap = new cv.VideoCapture(this.$refs.webcam.$refs.video);
      this.processTimer = requestAnimationFrame(this.processVideo);
    },
    processVideo() {
      this.videoCap.read(this.srcMat);
      const mat = new cv.Mat(this.height, this.width, cv.CV_8UC1);
      const laplacianSize = 3;
      cv.cvtColor(this.srcMat, mat, cv.COLOR_RGB2GRAY);
      cv.Laplacian(mat, this.dstC1Mat, cv.CV_8U, laplacianSize, 1, 0, cv.BORDER_DEFAULT);

      const myMean = new cv.Mat(1, 4, cv.CV_64F);
      const myStddev = new cv.Mat(1, 4, cv.CV_64F);
      cv.meanStdDev(this.dstC1Mat, myMean, myStddev);

      const blurriness = myStddev.doubleAt(0, 0);

      const date = new Date();
      const now = date.getTime();
      const IMG_SIZE = 224;
      const BLURRY_LIMIT = 30;

      if (blurriness < BLURRY_LIMIT) {
        this.helperText = 'Image is too blurry';
        if (!this.queryActive) {
          this.pctReal = 0;
          this.pctFake = 0;
        }
      }

      if (now - this.lastUploadTime > 1000
          && blurriness >= BLURRY_LIMIT
          && !this.queryActive) {
        console.log('Timer expired, uploading new image...');
        // Convert our mat buffer to base64 and run inference on it
        this.lastUploadTime = now;

        if (this.pctReal === 0) {
          this.helperText = '...';
        }

        // Inference only requires a small mat, so resize prior to upload to save bandwidth
        const frameScaled = new cv.Mat();
        const destSize = new cv.Size(IMG_SIZE, IMG_SIZE);
        cv.resize(this.srcMat, frameScaled, destSize, 0, 0, cv.INTER_AREA);
        // To get a Base64-encoded string for this data, blit it to a canvas first:
        //  https://answers.opencv.org/question/184937/converting-cvmat-data-to-mkv-format-via-opencvjs/
        // TODO: Find a non-canvas-way to do this:
        cv.imshow('imgcapture', frameScaled);

        const imgtype = 'image/png';
        // const imgtype = 'image/jpeg';

        // TODO: Use cv2.imencode to get the jpeg / png data from in-memory access
        //       rather than going to canvas and then toDataURL()
        let imgdata = document.getElementById('imgcapture').toDataURL(imgtype);
        imgdata = imgdata.replace(`data:${imgtype};base64,`, ''); // split off junk at the beginning
        // console.log("Image data is: " + imgdata);

        this.queryActive = true;
        axios.post(
          `${this.apiHost}/api/ImageEvaluation/image`,
          {
            imgData: imgdata,
            imgType: imgtype
          },
          {
            headers: { Authorization: `Bearer ${this.bearerToken}` }
          }
        ).then((result) => {
          console.log(` Real: ${result.data.pctReal}`);
          console.log(` Fake: ${result.data.pctFake}`);
          this.response = result.data;
          this.pctReal = result.data.pctReal;
          this.pctFake = result.data.pctFake;
          this.helperText = '';
          this.queryActive = false;
        }, (error) => {
          console.error(error);
        });
      }

      mat.delete();
      // cv.imshow("canvasOutput", this.srcMat);
      this.processTimer = requestAnimationFrame(this.processVideo);
    },
    onStarted(stream) {
      console.log('On Started Event', stream);
    },
    onStopped(stream) {
      console.log('On Stopped Event', stream);
    },
    onStop() {
      this.$refs.webcam.stop();
    },
    onStart() {
      this.$refs.webcam.start();
    },
    onError(error) {
      console.log('On Error Event', error);
    },
    onCameras(cameras) {
      this.devices = cameras;
      // Auto-select the first
      if (this.devices && this.devices.length > 0) {
        this.deviceId = this.devices[0].deviceId;
      }
      console.log('On Cameras Event', cameras);
    },
    onCameraChange(deviceId) {
      this.deviceId = deviceId;
      console.log('On Camera Change Event', deviceId);
    }
  }
};
</script>


<style scoped lang="scss">
.tight {
  margin: 1px;
  line-height: 1;
}
.inlinemarker {
  position: absolute;
  width: 3px;
  height: 30px;
  background: rgb(30,40,222);
}
.arrow-down {
  position: absolute;
  top: -5px;
  margin-left: -5px;
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-top: 7px solid rgb(30,40,222);
}
.arrow-up {
  position: absolute;
  top: 28px;
  margin-left: -5px;
  width: 0;
  height: 0;
  border-left: 7px solid transparent;
  border-right: 7px solid transparent;
  border-bottom: 7px solid rgb(30,40,222);
}
.gradientbar {
  position: relative;
  width: 100%;
  height: 30px;
  line-height: 30px;
  background: rgb(179,41,41);
  background: linear-gradient(90deg,
                              rgba(179,41,41,1) 0%,
                              rgba(222,228,27,1) 25%,
                              rgba(222,228,27,1) 75%,
                              rgba(78,215,74,1) 100%);
}
.gradientbarfaded {
  position: relative;
  width: 100%;
  height: 30px;
  line-height: 30px;
  background: rgb(177,161,161);
  background: linear-gradient(90deg,
                              rgba(177,161,161,1) 0%,
                              rgba(226,227,205,1) 25%,
                              rgba(226,227,205,1) 75%,
                              rgba(198,215,198,1) 100%);
}
</style>
